import React, { useState } from 'react';
import {
  Text,
  Badge,
  Spinner,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Sparkle20Regular,
  MoreHorizontal20Regular,
  Open16Regular,
  Copy16Regular,
  Delete16Regular,
} from '@fluentui/react-icons';
import DashboardThumbnail from './DashboardThumbnail';
import { ConnectorIcon } from '../../utils/connectorIcons';
import { formatRelativeTime } from '../../utils/relativeTime';
import { MutationOnly, useWorkspaceAccess } from '../security/WorkspaceAccess';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    cursor: 'pointer',
    transitionProperty: 'box-shadow, border-color, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease',
    position: 'relative',
    overflow: 'hidden',
    ':hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
      transform: 'translateY(-1px)',
    },
  },
  topRow: {
    display: 'flex',
    ...shorthands.padding('12px', '14px', '10px'),
    gap: '14px',
    alignItems: 'stretch',
  },
  thumbnailArea: {
    width: '100%',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('26px', '26px', '10px', '26px'),
  },
  metaColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    justifyContent: 'center',
    minWidth: 0,
    ...shorthands.overflow('hidden'),
    flex: '1 1 auto',
  },
  metaItem: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    display: 'block',
  },
  metaItemWithIcon: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  bottomSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    ...shorthands.padding('10px', '14px', '12px'),
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexWrap: 'wrap',
    marginTop: '6px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
  },
  metaRowItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    whiteSpace: 'nowrap',
  },
  connectionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
    maxWidth: '120px',
  },
  connectionName: {
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  metaDot: {
    color: tokens.colorNeutralStroke2,
    fontSize: '8px',
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    whiteSpace: 'normal',
    lineHeight: '1.3',
    letterSpacing: '-0.01em',
  },
  description: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.4',
  },
  timestamp: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    marginTop: '2px',
  },
  statusBadge: {
    marginLeft: '4px',
  },
  actionsButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: '150ms',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('6px'),
  },
  actionsVisible: {
    opacity: 1,
  },
});

export interface DashboardCardItem {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
  createdAt: Date;
  updatedAt: Date;
  chartCount: number;
  dataSourceName?: string | null;
  connectorId?: string | null;
  questions?: string[];
  thumbnailUrl?: string | null;
  sharedByName?: string;
}

interface DashboardCardProps {
  item: DashboardCardItem;
  onClick: () => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
  deleteLabel?: string;
  showActions?: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  item, onClick, onDuplicate, onDelete, deleteLabel = 'Delete', showActions = true,
}) => {
  const styles = useStyles();
  const { runMutation } = useWorkspaceAccess();
  const [hovered, setHovered] = useState(false);
  const isAiGenerated = item.status === 'ready' || item.status === 'generating';
  const isGenerating = item.status === 'generating' || item.status === 'cancel_requested';

  return (
    <div
      className={styles.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Thumbnail */}
      <div className={styles.thumbnailArea}>
        <DashboardThumbnail dashboardId={item.id} />
      </div>

      {/* Content */}
      <div className={styles.bottomSection}>
        <Text className={styles.title}>{item.name}</Text>
        {item.description && (
          <Text className={styles.description} title={item.description}>{item.description}</Text>
        )}
        <div className={styles.metaRow}>
          {item.sharedByName && (
            <span className={styles.metaRowItem}>Shared by {item.sharedByName}</span>
          )}
          {item.connectorId && (
            <span className={styles.connectionItem} title={item.dataSourceName ?? undefined}>
              <ConnectorIcon connectorId={item.connectorId} name={item.dataSourceName ?? ''} size={14} />
              <span className={styles.connectionName}>{item.dataSourceName}</span>
            </span>
          )}
          {item.chartCount > 0 && (
            <>
              {(item.sharedByName || item.connectorId) && <span className={styles.metaDot}>•</span>}
              <span className={styles.metaRowItem}>{item.chartCount} {item.chartCount === 1 ? 'Chart' : 'Charts'}</span>
            </>
          )}
          {isAiGenerated && (
            <>
              {(item.connectorId || item.chartCount > 0) && <span className={styles.metaDot}>•</span>}
              <span className={styles.metaRowItem}>
                <Sparkle20Regular style={{ fontSize: '11px' }} />
                AI Generated
              </span>
            </>
          )}
          {isGenerating && (
            <Badge appearance="tint" color="informative" icon={<Spinner size="extra-tiny" />} size="small">
              {item.status === 'cancel_requested' ? 'Canceling' : 'Generating'}
            </Badge>
          )}
          {item.status === 'failed' && (
            <Badge appearance="tint" color="danger" size="small">Failed</Badge>
          )}
          {item.status === 'canceled' && (
            <Badge appearance="tint" color="warning" size="small">Canceled</Badge>
          )}
        </div>
        <Text className={styles.timestamp}>Updated {formatRelativeTime(item.updatedAt)}</Text>
      </div>

      {/* Hover actions menu */}
      {showActions && <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button
            appearance="subtle"
            icon={<MoreHorizontal20Regular />}
            size="small"
            className={mergeClasses(styles.actionsButton, hovered && styles.actionsVisible)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Dashboard actions"
          />
        </MenuTrigger>
        <MenuPopover style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '8px' }}>
          <MenuList>
            <MenuItem icon={<Open16Regular />} onClick={(e) => { e.stopPropagation(); onClick(); }}>Open</MenuItem>
            <MutationOnly>
              {onDuplicate && (
                <MenuItem icon={<Copy16Regular />} onClick={(e) => { e.stopPropagation(); runMutation(() => onDuplicate(item.id)); }}>Duplicate</MenuItem>
              )}
              {onDelete && (
                <MenuItem icon={<Delete16Regular />} onClick={(e) => { e.stopPropagation(); runMutation(() => onDelete(item.id)); }}>{deleteLabel}</MenuItem>
              )}
            </MutationOnly>
          </MenuList>
        </MenuPopover>
      </Menu>}
    </div>
  );
};

export default DashboardCard;